import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import UsuarioSecundarioService from '../../modules/services/UsuarioSecundarioService';
import '../css/gestaoUsuarios.css';
import '../css/modal.css';

function GestaoUsuarios({ portalSession }) {
    // O login já traz essa lista pronta para o usuário master (RN009) — se ela chegou, a tela
    // renderiza com os dados na hora, sem esperar nenhuma consulta nova.
    const usuariosPreCarregados = portalSession?.usuariosSecundarios;
    const [usuarios, setUsuarios] = useState(usuariosPreCarregados || []);
    const [carregando, setCarregando] = useState(!usuariosPreCarregados);
    const [erro, setErro] = useState('');
    // { usuario, novoStatusAtivo } do usuário aguardando confirmação (ativar OU inativar).
    const [confirmacaoStatus, setConfirmacaoStatus] = useState(null);

    const souMaster = !!portalSession && (portalSession.cdUsuarioMaster === null || portalSession.cdUsuarioMaster === undefined);

    useEffect(() => {
        if (!souMaster) {
            setCarregando(false);
            return;
        }

        // Mesmo com a lista já pré-carregada do login, refaz a busca uma vez em segundo plano
        // (sem exibir "Carregando...") para refletir usuários criados/editados desde então —
        // sem isso a lista do login ficaria presa até um novo login.
        const carregar = async () => {
            try {
                const lista = await UsuarioSecundarioService.listarPorMaster(portalSession.usuario.cdUsuario);
                setUsuarios(lista || []);
            } catch (error) {
                if (!usuariosPreCarregados) {
                    setErro('Não foi possível carregar os usuários secundários.');
                }
            } finally {
                setCarregando(false);
            }
        };

        carregar();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [souMaster]);

    const alterarStatus = async (usuario) => {
        try {
            await UsuarioSecundarioService.atualizarStatus(usuario.cdUsuario, portalSession.usuario.cdUsuario, !usuario.ativo);
            setUsuarios((atual) =>
                atual.map((item) => (item.cdUsuario === usuario.cdUsuario ? { ...item, ativo: !item.ativo } : item))
            );
        } catch (error) {
            setErro(error.message || 'Não foi possível atualizar o status do usuário.');
        }
    };

    const handleAlternarStatus = (usuario) => {
        // Tanto ativar quanto inativar mudam o acesso do usuário ao portal — ambos passam por
        // confirmação antes de executar.
        setConfirmacaoStatus(usuario);
    };

    const confirmarAlternarStatus = async () => {
        if (!confirmacaoStatus) {
            return;
        }
        await alterarStatus(confirmacaoStatus);
        setConfirmacaoStatus(null);
    };

    if (!souMaster) {
        return (
            <div className="gestao-usuarios-page">
                <p className="gestao-usuarios-restrito">Esta área é exclusiva para o usuário master.</p>
            </div>
        );
    }

    return (
        <div className="dashboard">
            <div className="dashboard-content">
                <div className="dashboard-painel gestao-usuarios"> 
                        <div className="gestao-usuarios-header">
                            <h3 className='titulo'>Usuários e Permissões</h3>
                            <div className='topo-desc'>
                                 <Link to="/gestao-usuarios/novo" className="gestao-usuarios-btn-novo">
                                Criar usuário secundário
                            </Link>
                            </div>
                        </div> 

                        {erro && <div className="gestao-usuarios-erro">{erro}</div>}

                        {carregando ? (
                            <p>Carregando usuários...</p>
                        ) : usuarios.length === 0 ? (
                            <p>Nenhum usuário secundário cadastrado ainda.</p>
                        ) : (
                            <table className="gestao-usuarios-tabela">
                                <thead>
                                    <tr>
                                        <th>Nome</th>
                                        <th>Login</th>
                                        <th>E-mail</th>
                                        <th>Status</th>
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {usuarios.map((usuario) => (
                                        <tr key={usuario.cdUsuario}>
                                            <td>{usuario.nmUsuario}</td>
                                            <td>{usuario.dsLogin}</td>
                                            <td>{usuario.dsEmail}</td>
                                            <td>
                                                <span className={`gestao-usuarios-status ${usuario.ativo ? 'is-ativo' : 'is-inativo'}`}>
                                                    {usuario.ativo ? 'Ativo' : 'Inativo'}
                                                </span>
                                            </td>
                                            <td>
                                                <div className="gestao-usuarios-acoes-linha">
                                                    {usuario.ativo ? (
                                                        <Link to={`/gestao-usuarios/${usuario.cdUsuario}/editar`} className="gestao-usuarios-btn-toggle">
                                                            Editar
                                                        </Link>
                                                    ) : (
                                                        <button
                                                            type="button"
                                                            className="gestao-usuarios-btn-toggle is-disabled"
                                                            disabled
                                                            title="Reative o usuário para poder editá-lo"
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    <button className="gestao-usuarios-btn-toggle" onClick={() => handleAlternarStatus(usuario)}>
                                                        {usuario.ativo ? 'Inativar' : 'Ativar'}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        )}
                </div>
            </div>

            {confirmacaoStatus && (
                <div className="modal" onClick={() => setConfirmacaoStatus(null)}>
                    <div className="modal-content" onClick={(event) => event.stopPropagation()}>
                        <h3>{confirmacaoStatus.ativo ? 'Inativar usuário' : 'Ativar usuário'}</h3>
                        <p>
                            Tem certeza que deseja {confirmacaoStatus.ativo ? 'inativar' : 'ativar'}{' '}
                            <strong>{confirmacaoStatus.nmUsuario}</strong>?
                            {confirmacaoStatus.ativo
                                ? ' Ele perde o acesso ao portal imediatamente, até ser reativado.'
                                : ' Ele volta a poder acessar o portal imediatamente.'}
                        </p>
                        <div className="modal-actions">
                            <button type="button" className="cancel" onClick={() => setConfirmacaoStatus(null)}>
                                Cancelar
                            </button>
                            <button type="button" className="confirm" onClick={confirmarAlternarStatus}>
                                {confirmacaoStatus.ativo ? 'Inativar' : 'Ativar'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default GestaoUsuarios;
